import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Sidebar } from "../../components/Sidebar";
import { PageHeader, LoadingState, ErrorState } from "../../components/UI";
// Import direct plutôt que via le barrel `../../components/GreenMiles/HotelPage` : celui-ci
// réexporte SettingsStays, LuniButtonConfig & co, qui repartaient dans le chunk principal
// et annulaient leur chargement paresseux dans GreenMilesContent.
import { useGreenMilesHotelData } from "../../components/GreenMiles/HotelPage/hooks/useGreenMilesHotelData";
import DemosFilterTabs from "../../components/GreenMiles/Demos/DemosFilterTabs";
import DemoRoomsTable from "../../components/GreenMiles/Demos/DemoRoomsTable";
import SimpleItemsPanel from "../../components/GreenMiles/Demos/SimpleItemsPanel";
import QuickItemModal from "../../components/GreenMiles/Demos/QuickItemModal";
import { useGreenMilesDemos } from "../../components/GreenMiles/Demos/hooks/useGreenMilesDemos";
import { useResetDemo } from "../../components/GreenMiles/Demos/hooks/useResetDemo";
// import QrStatsCard from "../../components/GreenMiles/HotelPage/QrStatsCard"; // Deprecated pour la page Sales Démo
// import PeriodSelector from "../../components/GreenMiles/HotelPage/PeriodSelector"; // On reste sur "depuis le début"
import { formatNumber, formatInteger, formatPercentage } from "../../components/GreenMiles/utils/greenMilesUtils";

const API_URL = process.env.REACT_APP_API_URL || "/api";

function GreenMilesDemoPage() {
  const { id: hotelId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth.user?.access_token;
  const { fetchTemplates } = useGreenMilesDemos();
  const { resetDemo, loading: resetLoading, error: resetError } = useResetDemo();
  const [showEdit, setShowEdit] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrEditingRoom, setQrEditingRoom] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [localActions, setLocalActions] = useState([]);
  const [localRewards, setLocalRewards] = useState([]);
  const [movingItemId, setMovingItemId] = useState(null);
  const [movingDirection, setMovingDirection] = useState(null);
  const [movingOverItemId, setMovingOverItemId] = useState(null);

  // On supprime l'usage des stats QR sur la page démo Sales

  // Récupérer le paramètre de l'URL pour déterminer l'onglet actif
  const searchParams = new URLSearchParams(window.location.search);
  const tabParam = searchParams.get("tab");
  const [activeFilter, setActiveFilter] = useState(tabParam === "actions" ? "actions" : tabParam === "rewards" ? "rewards" : "rooms");

  // Utilisation du hook personnalisé
  const {
    hotel,
    rooms,
    actions,
    rewards,
    statistics,

    loading,
    periodLoading,
    error,
    // Période
    selectedPeriod,
    handlePeriodChange,
    // Tri des chambres
    sortState,
    handleSort,
    // Filtrage des actions par type
    selectedActionTypes,
    availableActionTypes,
    handleActionTypeToggle,
    handleClearAllActionTypes,
    handleSelectAllActionTypes,
    // Filtrage des rewards par type
    selectedRewardTypes,
    availableRewardTypes,
    handleRewardTypeToggle,
    handleClearAllRewardTypes,
    handleSelectAllRewardTypes,
    // Toggle hidden status
    toggleActionHidden,
    toggleRewardHidden,
    refetch,
  } = useGreenMilesHotelData(hotelId);

  // Pas de stats QR à charger ici

  const handleGoBack = () => {
    navigate("/greenmiles/demos");
  };

  const orderedActions = [...(actions || [])].sort((a, b) => {
    const posA = Number(a?.acPosition);
    const posB = Number(b?.acPosition);
    return (Number.isFinite(posA) ? posA : 999999) - (Number.isFinite(posB) ? posB : 999999);
  });

  const orderedRewards = [...(rewards || [])].sort((a, b) => {
    const posA = Number(a?.rwPosition);
    const posB = Number(b?.rwPosition);
    return (Number.isFinite(posA) ? posA : 999999) - (Number.isFinite(posB) ? posB : 999999);
  });

  const actionsSignature = useMemo(() => orderedActions.map((a) => `${a.acId || a.actId}:${a.acPosition ?? ""}`).join("|"), [orderedActions]);
  const rewardsSignature = useMemo(() => orderedRewards.map((r) => `${r.rwId}:${r.rwPosition ?? ""}`).join("|"), [orderedRewards]);

  useEffect(() => {
    setLocalActions(orderedActions);
  }, [actionsSignature]);

  useEffect(() => {
    setLocalRewards(orderedRewards);
  }, [rewardsSignature]);

  const handleResetDemo = async () => {
    try {
      await resetDemo(hotelId);
      setShowResetConfirm(false);
      // Recharger les données après le reset
      window.location.reload();
    } catch (error) {
      console.error("Erreur lors du reset de la démo:", error);
    }
  };

  // États de chargement et d'erreur
  if (loading) {
    return <LoadingState message="Chargement des données GreenMiles..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!hotel) {
    return <ErrorState error="Hôtel GreenMiles non trouvé" />;
  }

  const TemplatesPanel = ({ fetchTemplates, hotelId, activeTab, token, existingActions = [], existingRewards = [] }) => {
    const [templates, setTemplates] = useState({ actions: [], rewards: [] });
    const [loadingTpl, setLoadingTpl] = useState(false);
    const [errorTpl, setErrorTpl] = useState(null);
    const [query, setQuery] = useState("");
    const [addingItems, setAddingItems] = useState(new Set());

    useEffect(() => {
      let mounted = true;
      const load = async () => {
        try {
          setLoadingTpl(true);
          setErrorTpl(null);
          const data = await fetchTemplates();
          if (mounted) setTemplates(data);
        } catch (e) {
          if (mounted) setErrorTpl("Erreur lors du chargement des templates");
        } finally {
          if (mounted) setLoadingTpl(false);
        }
      };
      load();
      return () => {
        mounted = false;
      };
    }, [fetchTemplates]);

    const handleAddFromTemplate = async (tpl) => {
      const isAction = activeTab === "actions";
      const itemKey = `${tpl.key}-${isAction ? "action" : "reward"}`;

      // Empêcher l'ajout si déjà présent ou en cours d'ajout
      if (isTemplateAlreadyAdded(tpl, isAction) || addingItems.has(itemKey)) return;

      // Marquer comme en cours d'ajout
      setAddingItems((prev) => new Set([...prev, itemKey]));

      const url = isAction ? `${API_URL}/greenmiles/hotels/${hotelId}/actions/simple` : `${API_URL}/greenmiles/hotels/${hotelId}/rewards/simple`;
      const payload = isAction
        ? {
            titleFr: tpl.titleFr,
            descriptionFr: tpl.descriptionFr,
            titleBFr: tpl.titleBFr,
            subtitleFr: tpl.subtitleFr,
            titleEn: tpl.titleEn,
            descriptionEn: tpl.descriptionEn,
            titleBEn: tpl.titleBEn,
            subtitleEn: tpl.subtitleEn,
            titleAr: tpl.titleAr,
            descriptionAr: tpl.descriptionAr,
            titleBAr: tpl.titleBAr,
            subtitleAr: tpl.subtitleAr,
            // Questions pour types qui en nécessitent (si présentes dans le template)
            questionFr: tpl.questionFr,
            questionEn: tpl.questionEn,
            questionAr: tpl.questionAr,
            imageUrl: tpl.imageUrl,
            iconUrl: tpl.iconUrl,
            type: tpl.type,
            rewardPoints: tpl.defaults?.rewardPoints,
            titleColor: tpl.defaults?.titleColor,
            ...(tpl.type === "YESNO" || tpl.type === "YESNOSTAY" || tpl.type === "YESNOMAIL"
              ? { actPositiveResponse: tpl.defaults?.actPositiveResponse ?? "YES" }
              : {}),
            ...(tpl.type === "LINK"
              ? {
                  linkButtonTextFr: tpl.linkButtonTextFr,
                  linkButtonTextEn: tpl.linkButtonTextEn,
                  linkButtonTextAr: tpl.linkButtonTextAr,
                }
              : {}),
            ...(tpl.type === "QUIZ" && tpl.quizData ? { quizData: tpl.quizData } : {}),
          }
        : {
            titleFr: tpl.titleFr,
            descriptionFr: tpl.descriptionFr,
            subtitleFr: tpl.subtitleFr,
            titleEn: tpl.titleEn,
            descriptionEn: tpl.descriptionEn,
            subtitleEn: tpl.subtitleEn,
            titleAr: tpl.titleAr,
            descriptionAr: tpl.descriptionAr,
            subtitleAr: tpl.subtitleAr,
            imageUrl: tpl.imageUrl,
            iconUrl: tpl.iconUrl,
            type: tpl.type,
            cost: tpl.defaults?.cost,
            waterVolume: tpl.defaults?.waterVolume,
            titleColor: tpl.defaults?.titleColor,
          };

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Erreur ${response.status}`);
        }

        await refetch();
      } catch (error) {
        console.error("Erreur lors de l'ajout du template:", error);
        // Optionnel: afficher une notification d'erreur à l'utilisateur
      } finally {
        // Retirer de la liste des éléments en cours d'ajout
        setAddingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemKey);
          return newSet;
        });
      }
    };

    // Déterminer si un template a déjà été ajouté (on compare type + titre FR)
    const normalize = (s) => (s || "").toString().trim().toLowerCase();
    const isTemplateAlreadyAdded = (tpl, isAction) => {
      if (isAction) {
        return existingActions.some((a) => normalize(a.acType) === normalize(tpl.type) && normalize(a.display_name || a.acTitle || a.name) === normalize(tpl.titleFr));
      }
      return existingRewards.some((r) => normalize(r.rwType) === normalize(tpl.type) && normalize(r.display_name || r.rwName || r.name) === normalize(tpl.titleFr));
    };

    // Liste filtrée selon la recherche
    const normalizeText = (s) =>
      (s || "")
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    const listAll = activeTab === "actions" ? templates.actions : templates.rewards;
    const list = listAll.filter((tpl) => {
      const hay = normalizeText(`${tpl.titleFr || ""} ${tpl.descriptionFr || ""} ${tpl.titleEn || ""} ${tpl.descriptionEn || ""} ${tpl.type || ""}`);
      const q = normalizeText(query);
      return hay.includes(q);
    });

    const handleCreateManually = () => {
      const isAction = activeTab === "actions";
      const url = isAction ? `/hotels/${hotelId}/greenmiles/create-action-v2` : `/hotels/${hotelId}/greenmiles/create-reward-v2`;
      window.location.href = url;
    };

    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900">Templates {activeTab === "actions" ? "Actions" : "Récompenses"}</h3>
            <div className="flex items-center gap-2">
              <button onClick={handleCreateManually} className="px-3 py-2 text-sm font-medium text-green-600 hover:text-green-700 border border-green-300 rounded-md hover:bg-green-50 transition-colors">
                Créer manuellement →
              </button>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher (FR/EN, type, description)" className="w-64 max-w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#365644]" type="text" />
            </div>
          </div>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto">
          {loadingTpl ? (
            <div className="text-sm text-gray-500">Chargement des templates...</div>
          ) : errorTpl ? (
            <div className="text-sm text-red-600">{errorTpl}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {list.map((tpl, idx) => {
                const previewImageUrl = tpl.iconUrl || tpl.imageUrl;
                const useIconPreview = Boolean(tpl.iconUrl);

                return (
                  <div key={`${tpl.key}-${idx}`} className="border rounded-md p-3 flex items-start justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                      {previewImageUrl ? <img src={previewImageUrl} alt="visuel" className={`w-10 h-10 rounded-md flex-shrink-0 ${useIconPreview ? "object-contain bg-white border border-gray-100 p-1" : "object-cover"}`} /> : <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center text-gray-400 text-xs flex-shrink-0">N/A</div>}
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 whitespace-normal break-words">{tpl.titleFr}</div>
                        <div className="text-sm text-gray-500 whitespace-normal break-words">{tpl.subtitleFr || tpl.descriptionFr}</div>
                      </div>
                    </div>
                    {(() => {
                      const isAction = activeTab === "actions";
                      const itemKey = `${tpl.key}-${isAction ? "action" : "reward"}`;
                      const already = isTemplateAlreadyAdded(tpl, isAction);
                      const isAdding = addingItems.has(itemKey);
                      const isDisabled = already || isAdding;

                      return (
                        <button onClick={() => handleAddFromTemplate(tpl)} disabled={isDisabled} className={`px-3 py-1.5 rounded-md text-sm ${isDisabled ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`} title={already ? "Déjà ajouté" : isAdding ? "Ajout en cours..." : "Ajouter"}>
                          {already ? "Ajouté" : isAdding ? "Ajout..." : "Ajouter"}
                        </button>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-6 overflow-auto">
        <PageHeader
          title={`${hotel.name} - GreenMiles (Démo)`}
          onGoBack={handleGoBack}
          actions={
            <div className="flex space-x-2">
              <button onClick={() => setShowResetConfirm(true)} disabled={resetLoading} className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                {resetLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Réinitialisation...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset Démo
                  </div>
                )}
              </button>
            </div>
          }
        />
        {/* Plus de sélecteur de période: toujours "depuis le début" */}
        {/* Section des statistiques principales */}
        {statistics && (
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total séjours */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total séjours</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatInteger(statistics.total_stays || 0)}</p>
                </div>
              </div>
            </div>

            {/* Actions disponibles */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h9.586l4-4V7a2 2 0 00-2-2H15m-6 0V3a2 2 0 012-2h2a2 2 0 012 2v2m-6 0h6" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Actions disponibles</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatInteger(statistics.actions_count || 0)}</p>
                </div>
              </div>
            </div>

            {/* Récompenses disponibles */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002-2h10a2 2 0 002-2v-7" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Récompenses disponibles</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatInteger(statistics.rewards_count || 0)}</p>
                </div>
              </div>
            </div>

            {/* Carte QR supprimée pour la page démo Sales */}
          </div>
        )}
        {/* Section détaillée supprimée: on garde uniquement les 3 cartes principales ci-dessus */}
        <div className="space-y-6">
          {/* Layout Sales: colonne gauche (Gestion simple) + colonne droite (mock téléphone) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <DemosFilterTabs activeFilter={activeFilter} onFilterChange={setActiveFilter} />
              {activeFilter !== "rooms" ? (
                <SimpleItemsPanel
                  type={activeFilter}
                  items={activeFilter === "actions" ? localActions : localRewards}
                  movingItemId={movingItemId}
                  movingDirection={movingDirection}
                  movingOverItemId={movingOverItemId}
                  onEdit={(item) => {
                    setEditingItem(item);
                    setShowEdit(true);
                  }}
                  onMove={async (item, direction) => {
                    if (movingItemId) return;
                    try {
                      const isAction = activeFilter === "actions";
                      const id = isAction ? item.acId || item.actId : item.rwId;
                      const list = isAction ? localActions : localRewards;
                      const currentPosition = list.findIndex((entry) => (isAction ? entry.acId || entry.actId : entry.rwId) === id);
                      const newPosition = direction === "up" ? currentPosition - 1 : currentPosition + 1;

                      if (currentPosition === -1 || newPosition < 0 || newPosition >= list.length) return;

                      const targetId = isAction ? list[newPosition].acId || list[newPosition].actId : list[newPosition].rwId;
                      setMovingItemId(id);
                      setMovingDirection(direction);
                      setMovingOverItemId(targetId);
                      await new Promise((resolve) => window.setTimeout(resolve, 280));

                      const nextList = [...list];
                      [nextList[currentPosition], nextList[newPosition]] = [nextList[newPosition], nextList[currentPosition]];
                      if (isAction) {
                        setLocalActions(nextList);
                      } else {
                        setLocalRewards(nextList);
                      }

                      const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/actions/${id}/reorder`, {
                        method: "PATCH",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                          direction,
                          currentPosition,
                          newPosition,
                          ...(isAction ? {} : { type: "reward" }),
                        }),
                      });

                      if (!response.ok) {
                        const errorData = await response.json();
                        if (isAction) {
                          setLocalActions(list);
                        } else {
                          setLocalRewards(list);
                        }
                        throw new Error(errorData.message || `Erreur ${response.status}`);
                      }
                    } catch (error) {
                      console.error("Erreur lors du déplacement:", error);
                    } finally {
                      setMovingItemId(null);
                      setMovingDirection(null);
                      setMovingOverItemId(null);
                    }
                  }}
                  onDelete={async (item) => {
                    const isAction = activeFilter === "actions";
                    const id = isAction ? item.acId || item.actId : item.rwId;
                    const url = isAction ? `${API_URL}/greenmiles/hotels/${hotelId}/actions/${id}` : `${API_URL}/greenmiles/hotels/${hotelId}/rewards/${id}`;
                    await fetch(url, {
                      method: "DELETE",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    refetch();
                  }}
                />
              ) : null}

              {/* Bibliothèque de templates (actions/rewards) */}
              {activeFilter !== "rooms" && <TemplatesPanel fetchTemplates={fetchTemplates} hotelId={hotelId} activeTab={activeFilter} token={token} existingActions={actions} existingRewards={rewards} />}
              {activeFilter === "rooms" && (
                <DemoRoomsTable
                  rooms={rooms}
                  sortState={sortState}
                  handleSort={handleSort}
                  onEditQr={(room) => {
                    setQrEditingRoom(room);
                    setShowQrModal(true);
                  }}
                />
              )}
            </div>
            <div className="">
              {/* Mock téléphone pour l'iFrame PWA (placeholder) */}
              <div className="mx-auto w-[360px] h-[740px] rounded-[40px] bg-gray-900 relative shadow-2xl border-8 border-gray-800">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-gray-800 rounded-b-2xl"></div>
                <div className="w-full h-full bg-white rounded-[28px] overflow-hidden">
                  <div className="h-full flex items-center justify-center text-gray-500 text-sm p-4 text-center">
                    Aperçu mobile GreenMiles
                    <br />
                    (l'iframe sera intégré ici)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modals */}
          <QuickItemModal
            isOpen={showEdit}
            onClose={() => {
              setShowEdit(false);
              setEditingItem(null);
            }}
            type={activeFilter}
            hotelId={hotelId}
            token={token}
            initial={editingItem}
            onSuccess={refetch}
          />

          {showQrModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">{qrEditingRoom?.has_qr_code === 1 ? "Modifier" : "Ajouter"} un QR</h3>
                  <button onClick={() => setShowQrModal(false)} className="text-gray-400 hover:text-gray-600">
                    ✕
                  </button>
                </div>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const qrId = form.qrId.value.trim();
                    if (!qrId) return;
                    await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/rooms/${qrEditingRoom.rId || qrEditingRoom.id}/qr`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ qrId }),
                    });
                    setShowQrModal(false);
                    setQrEditingRoom(null);
                    refetch();
                  }}
                  className="p-5 space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">QR ID</label>
                    <input name="qrId" defaultValue={qrEditingRoom?.qrId || ""} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#365644]" placeholder="Ex: abc123" required />
                  </div>
                  <div className="pt-2 flex items-center justify-end space-x-2">
                    <button type="button" onClick={() => setShowQrModal(false)} className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                      Annuler
                    </button>
                    <button type="submit" className="px-3 py-2 text-sm rounded-md bg-[#365644] text-white hover:bg-[#2d4a3a]">
                      Enregistrer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Modal de confirmation de reset */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">Confirmer la réinitialisation</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">Êtes-vous sûr de vouloir réinitialiser cette démo ? Cette action supprimera définitivement :</p>
                  <ul className="text-sm text-gray-500 mt-2 text-left list-disc list-inside">
                    <li>Tous les séjours (stays) et leurs données</li>
                    <li>Toutes les actions effectuées</li>
                    <li>Toutes les récompenses utilisées</li>
                    <li>Tous les scans QR</li>
                    <li>Tous les profils utilisateurs</li>
                  </ul>
                  <p className="text-sm font-medium text-red-600 mt-2">Cette action est irréversible !</p>
                </div>
                <div className="items-center px-4 py-3">
                  <button onClick={handleResetDemo} disabled={resetLoading} className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed">
                    {resetLoading ? "Reset..." : "Reset"}
                  </button>
                  <button onClick={() => setShowResetConfirm(false)} disabled={resetLoading} className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
                    Annuler
                  </button>
                </div>
                {resetError && <div className="mt-2 text-sm text-red-600">{resetError}</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GreenMilesDemoPage;
